// app/(public)/signup/page.tsx  (or wherever your SignUpPage lives)
'use client';

import GoogleSignIn from '@/components/GoogleSignIn';
import { useSignUp } from '@/hooks/useSignUp';
import { Alert, Box, Button, Checkbox, Container, FormControlLabel, Link as MuiLink, Paper, Stack, TextField, Typography } from '@mui/material';
import NextLink from 'next/link';
import { useState } from 'react';
const SignUpForm = ({
  isInModal = false
}: {
  isInModal?: boolean;
}) => {
  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: ''
  });
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const mutation = useSignUp(() => {
    setMessage('Sign-up successful! You can now log in.');
    setSuccess(true);
    setForm({
      email: '',
      password: '',
      firstName: '',
      lastName: ''
    });
    setAcceptTerms(false);
  }, error => {
    const errMsg = error?.response?.data?.error || 'Something went wrong during sign-up';
    setMessage(errMsg);
    setSuccess(false);
  });
  const validatePassword = (password: string) => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/.test(password);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);
    if (!validatePassword(form.password)) {
      setMessage('Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.');
      return;
    }
    if (!acceptTerms) {
      setMessage('You must agree to our Terms of Service and Privacy Policy.');
      setSuccess(false);
      return;
    }
    mutation.mutate(form);
  };
  const allValid = form.firstName.trim() && form.lastName.trim() && form.email.trim() && validatePassword(form.password) && acceptTerms;
  const renderMainForm = () => {
    return <Box data-sentry-element="Box" data-sentry-component="renderMainForm" data-sentry-source-file="SignUpForm.tsx">
        <GoogleSignIn context="signup" data-sentry-element="GoogleSignIn" data-sentry-source-file="SignUpForm.tsx" />
        <Box component="form" onSubmit={handleSubmit} noValidate mt={1} data-sentry-element="Box" data-sentry-source-file="SignUpForm.tsx">
          <Stack spacing={3} data-sentry-element="Stack" data-sentry-source-file="SignUpForm.tsx">
            <TextField label="First Name" variant="outlined" fullWidth value={form.firstName} onChange={e => setForm({
            ...form,
            firstName: e.target.value
          })} required data-sentry-element="TextField" data-sentry-source-file="SignUpForm.tsx" />
            <TextField label="Last Name" variant="outlined" fullWidth value={form.lastName} onChange={e => setForm({
            ...form,
            lastName: e.target.value
          })} required data-sentry-element="TextField" data-sentry-source-file="SignUpForm.tsx" />
            <TextField label="Email" type="email" variant="outlined" fullWidth value={form.email} onChange={e => setForm({
            ...form,
            email: e.target.value
          })} required data-sentry-element="TextField" data-sentry-source-file="SignUpForm.tsx" />
            <TextField label="Password" type="password" variant="outlined" fullWidth value={form.password} onChange={e => setForm({
            ...form,
            password: e.target.value
          })} helperText="At least 8 characters, including uppercase, lowercase, number & special char." required data-sentry-element="TextField" data-sentry-source-file="SignUpForm.tsx" />

            <FormControlLabel control={<Checkbox checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)} />} label={<Typography variant="body2">
                  I agree to the
                  {' '}
                  <NextLink href="/terms" passHref>
                    <MuiLink target="_blank" underline="hover">
                      Terms of Service
                    </MuiLink>
                  </NextLink>
                  {' and '}
                  <NextLink href="/privacy-policy" passHref>
                    <MuiLink target="_blank" underline="hover">
                      Privacy Policy
                    </MuiLink>
                  </NextLink>
                </Typography>} data-sentry-element="FormControlLabel" data-sentry-source-file="SignUpForm.tsx" />
            <Button type="submit" variant="contained" color="primary" fullWidth size="large" disabled={!allValid} sx={{
            fontWeight: 600,
            pt: 1.5,
            pb: 1.5
          }} data-sentry-element="Button" data-sentry-source-file="SignUpForm.tsx">
              Create Account
            </Button>
          </Stack>
        </Box>

        {message && <Alert severity={success ? 'success' : 'error'} sx={{
        mt: 3,
        borderRadius: 2
      }}>
            {message}
          </Alert>}
      </Box>;
  };
  if (isInModal) {
    return renderMainForm();
  }
  return <Container maxWidth="sm" sx={{
    py: 8
  }} data-sentry-element="Container" data-sentry-component="SignUpForm" data-sentry-source-file="SignUpForm.tsx">
      <Paper elevation={3} sx={{
      p: 4,
      borderRadius: 3
    }} data-sentry-element="Paper" data-sentry-source-file="SignUpForm.tsx">
        <Typography variant="h4" fontWeight={700} align="center" gutterBottom data-sentry-element="Typography" data-sentry-source-file="SignUpForm.tsx">
          Create Your Account
        </Typography>
        {renderMainForm()}
      </Paper>
    </Container>;
};
export default SignUpForm;